// User Profile Page
// Displays user information, their submitted issues and community notes

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';

interface ClassIssue {
  id: string;
  class_name: string;
  issue_type: string;
  title: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  created_at: string;
  updated_at: string;
  comment_count: number;
}

interface IssueComment {
  id: string;
  issue_id: string;
  user_id: string;
  username: string;
  avatar: string;
  is_moderator: number;
  comment: string;
  is_moderator_note: number;
  created_at: string;
}

interface CommunityNote {
  id: string;
  class_name: string;
  method_name?: string;
  property_name?: string;
  content: string;
  status: 'pending' | 'approved' | 'rejected';
  rejection_reason?: string;
  created_at: string;
  updated_at: string;
  approved_at?: string;
}

const issueTypeLabels: Record<string, string> = {
  incorrect_info: '❌ Incorrect Info',
  missing_info: '📝 Missing Info',
  broken_link: '🔗 Broken Link',
  typo: '✏️ Typo/Grammar',
  other: '❓ Other'
};

const statusColors: Record<string, string> = {
  open: '#007bff',
  in_progress: '#ffc107',
  resolved: '#28a745',
  closed: '#6c757d'
};

const priorityColors: Record<string, string> = {
  low: '#6c757d',
  medium: '#007bff',
  high: '#fd7e14',
  critical: '#dc3545'
};

const Profile: React.FC = () => {
  const { user, isAuthenticated, loading, getAuthToken } = useAuth();
  const [activeTab, setActiveTab] = useState<'issues' | 'notes'>('issues');

  // Issues state
  const [issues, setIssues] = useState<ClassIssue[]>([]);
  const [loadingIssues, setLoadingIssues] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedIssue, setSelectedIssue] = useState<ClassIssue | null>(null);
  const [issueDetails, setIssueDetails] = useState<{ issue: ClassIssue, comments: IssueComment[] } | null>(null);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Notes state
  const [notes, setNotes] = useState<CommunityNote[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [notesStatusFilter, setNotesStatusFilter] = useState<string>('');
  const [selectedNote, setSelectedNote] = useState<CommunityNote | null>(null);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchUserIssues();
      fetchUserNotes();
    }
  }, [isAuthenticated, user]);

  const fetchUserIssues = async () => {
    setLoadingIssues(true);
    try {
      const token = getAuthToken();
      const response = await fetch('/api/issues/user', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setIssues(data.issues || []);
      }
    } catch (error) {
      logger.error('Failed to fetch user issues:', error);
    } finally {
      setLoadingIssues(false);
    }
  };

  const fetchUserNotes = async () => {
    setLoadingNotes(true);
    try {
      const token = getAuthToken();
      const response = await fetch('/api/notes/user', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotes(data.notes || []);
      }
    } catch (error) {
      logger.error('Failed to fetch user notes:', error);
    } finally {
      setLoadingNotes(false);
    }
  };

  const fetchIssueDetails = async (issueId: string) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`/api/issues/details?issue_id=${issueId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      const data = await response.json();
      setIssueDetails(data);
    } catch (error) {
      logger.error('Failed to fetch issue details:', error);
    }
  };

  const handleSelectIssue = (issue: ClassIssue) => {
    setSelectedIssue(issue);
    fetchIssueDetails(issue.id);
  };

  const handleAddComment = async () => {
    if (!selectedIssue || !newComment.trim()) return;
    setSubmitting(true);

    try {
      const token = getAuthToken();
      const response = await fetch('/api/issues/comment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          issue_id: selectedIssue.id,
          comment: newComment
        })
      });

      if (response.ok) {
        setNewComment('');
        fetchIssueDetails(selectedIssue.id);
        fetchUserIssues();
      }
    } catch (error) {
      logger.error('Failed to add comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseIssue = async () => {
    if (!selectedIssue) return;
    if (!confirm('Are you sure you want to close this issue?')) return;

    setSubmitting(true);
    try {
      const token = getAuthToken();
      const response = await fetch('/api/issues/close', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ issue_id: selectedIssue.id })
      });

      if (response.ok) {
        setSelectedIssue(null);
        fetchUserIssues();
      }
    } catch (error) {
      logger.error('Failed to close issue:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!confirm('Are you sure you want to delete this note? This action cannot be undone.')) return;

    try {
      const token = getAuthToken();
      const response = await fetch(`/api/notes/delete/${noteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSelectedNote(null);
        fetchUserNotes();
      } else {
        const data = await response.json();
        alert(`Failed to delete note: ${data.error}`);
      }
    } catch (error) {
      logger.error('Failed to delete note:', error);
      alert('Failed to delete note. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  }

  if (!isAuthenticated || !user) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You must be logged in to view your profile.</p>
      </div>
    );
  }

  const filteredIssues = statusFilter
    ? issues.filter(issue => issue.status === statusFilter)
    : issues;

  const filteredNotes = notesStatusFilter
    ? notes.filter(note => note.status === notesStatusFilter)
    : notes;

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* User Info Card */}
      <div style={{
        background: 'var(--ifm-background-surface-color)',
        border: '1px solid var(--ifm-color-emphasis-300)',
        borderRadius: '12px',
        padding: '2rem',
        marginBottom: '2rem',
        display: 'flex',
        alignItems: 'center',
        gap: '2rem',
        flexWrap: 'wrap'
      }}>
        <img
          src={user.avatar}
          alt={user.username}
          style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            border: '3px solid var(--ifm-color-primary)'
          }}
        />
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: '0 0 0.5rem 0', color: 'var(--ifm-font-color-base)' }}>
            {user.username}
          </h1>
          {user.is_moderator && (
            <p style={{ margin: '0 0 0.5rem 0', color: 'var(--ifm-color-primary)', fontSize: '13px', fontWeight: 600 }}>
              👑 Moderator
            </p>
          )}
          <p style={{ margin: '0', color: 'var(--ifm-color-emphasis-700)', fontSize: '14px' }}>
            Signed in via {user.provider === 'github' ? 'GitHub' : 'Discord'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--ifm-color-primary)' }}>
              {user.contributions?.notes || 0}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--ifm-color-emphasis-600)' }}>
              💬 Notes
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--ifm-color-primary)' }}>
              {user.contributions?.issues || 0}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--ifm-color-emphasis-600)' }}>
              🐛 Issues
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '2rem', borderBottom: '2px solid var(--ifm-color-emphasis-300)' }}>
        <button
          onClick={() => setActiveTab('issues')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'issues' ? 'var(--ifm-color-primary)' : 'transparent',
            color: activeTab === 'issues' ? 'white' : 'var(--ifm-font-color-base)',
            border: 'none',
            borderBottom: activeTab === 'issues' ? '2px solid var(--ifm-color-primary)' : 'none',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: activeTab === 'issues' ? 'bold' : 'normal'
          }}
        >
          🐛 My Issues ({issues.length})
        </button>
        <button
          onClick={() => setActiveTab('notes')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'notes' ? 'var(--ifm-color-primary)' : 'transparent',
            color: activeTab === 'notes' ? 'white' : 'var(--ifm-font-color-base)',
            border: 'none',
            borderBottom: activeTab === 'notes' ? '2px solid var(--ifm-color-primary)' : 'none',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: activeTab === 'notes' ? 'bold' : 'normal'
          }}
        >
          📝 My Notes ({notes.length})
        </button>
      </div>

      {/* Issues Tab */}
      {activeTab === 'issues' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
            <h2 style={{ margin: 0 }}>Reported Issues ({filteredIssues.length})</h2>
            <div>
              <label style={{ marginRight: '0.5rem', fontSize: '14px' }}>Filter:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid var(--ifm-color-emphasis-300)',
                  background: 'var(--ifm-background-color)',
                  color: 'var(--ifm-font-color-base)'
                }}
              >
                <option value="">All Status</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>

          {loadingIssues ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--ifm-color-emphasis-600)' }}>
              Loading issues...
            </div>
          ) : filteredIssues.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              background: 'var(--ifm-background-surface-color)',
              border: '1px solid var(--ifm-color-emphasis-300)',
              borderRadius: '8px',
              color: 'var(--ifm-color-emphasis-600)'
            }}>
              {statusFilter ? `No ${statusFilter} issues found.` : 'You haven\'t reported any issues yet.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {filteredIssues.map((issue) => (
                <div
                  key={issue.id}
                  onClick={() => handleSelectIssue(issue)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    border: '1px solid var(--ifm-color-emphasis-300)',
                    borderRadius: '6px',
                    background: 'var(--ifm-background-surface-color)',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--ifm-color-primary)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--ifm-color-emphasis-300)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                    {/* Left: Title and metadata with inline badges */}
                    <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
                    <h3 style={{ margin: '0 0 0.15rem 0', fontSize: '14px', fontWeight: '600' }}>{issue.title}</h3>
                    <div style={{ fontSize: '11px', color: 'var(--ifm-color-emphasis-600)' }}>
                      {issue.class_name} · {formatDate(issue.created_at)}
                    </div>
                    <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.15rem', flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: '10px',
                        padding: '0.1rem 0.35rem',
                        borderRadius: '3px',
                        backgroundColor: statusColors[issue.status],
                        color: 'white',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>
                        {issue.status}
                      </span>
                      <span style={{
                        fontSize: '10px',
                        padding: '0.1rem 0.35rem',
                        borderRadius: '3px',
                        backgroundColor: priorityColors[issue.priority],
                        color: 'white',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>
                        {issue.priority}
                      </span>
                      <span style={{
                        fontSize: '10px',
                      padding: '0.1rem 0.35rem',
                      borderRadius: '3px',
                      backgroundColor: 'var(--ifm-color-emphasis-200)',
                      color: 'var(--ifm-color-emphasis-800)',
                      fontWeight: '600',
                      whiteSpace: 'nowrap'
                    }}>
                      {issueTypeLabels[issue.issue_type]}
                    </span>
                    </div>
                  </div>

                  {/* Middle: Description preview */}
                  <div style={{
                    flex: '2 1 250px',
                    fontSize: '12px',
                    color: 'var(--ifm-color-emphasis-700)',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: '1.3'
                  }}>
                    {issue.description}
                  </div>

                  {/* Right: Comment count */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    marginLeft: 'auto',
                    fontSize: '11px',
                    color: 'var(--ifm-color-emphasis-600)',
                    fontWeight: '600'
                  }}>
                    <span>{issue.comment_count} 💬</span>
                  </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Notes Tab */}
      {activeTab === 'notes' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
            <h2 style={{ margin: 0 }}>My Community Notes ({filteredNotes.length})</h2>
            <div>
              <label style={{ marginRight: '0.5rem', fontSize: '14px' }}>Filter:</label>
              <select
                value={notesStatusFilter}
                onChange={(e) => setNotesStatusFilter(e.target.value)}
                style={{
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid var(--ifm-color-emphasis-300)',
                  background: 'var(--ifm-background-color)',
                  color: 'var(--ifm-font-color-base)'
                }}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          {loadingNotes ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--ifm-color-emphasis-600)' }}>
              Loading notes...
            </div>
          ) : filteredNotes.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              background: 'var(--ifm-background-surface-color)',
              border: '1px solid var(--ifm-color-emphasis-300)',
              borderRadius: '8px',
              color: 'var(--ifm-color-emphasis-600)'
            }}>
              {notesStatusFilter ? `No ${notesStatusFilter} notes found.` : 'You haven\'t submitted any notes yet.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {filteredNotes.map((note) => {
                const noteTarget = note.method_name
                  ? `${note.class_name}::${note.method_name}()`
                  : note.property_name
                  ? `${note.class_name}.${note.property_name}`
                  : note.class_name;

                return (
                  <div
                    key={note.id}
                    style={{
                      padding: '0.5rem 0.75rem',
                      border: '1px solid var(--ifm-color-emphasis-300)',
                      borderRadius: '6px',
                      background: 'var(--ifm-background-surface-color)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      flexWrap: 'wrap'
                    }}
                  >
                    {/* Left section: Title and status */}
                    <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
                      <h3 style={{ margin: '0 0 0.15rem 0', fontSize: '14px', fontWeight: '600' }}>{noteTarget}</h3>
                      <div style={{ fontSize: '11px', color: 'var(--ifm-color-emphasis-600)' }}>
                        {formatDate(note.created_at)}
                      </div>
                      <div style={{ marginTop: '0.15rem' }}>
                        <span style={{
                          fontSize: '10px',
                          padding: '0.1rem 0.35rem',
                          borderRadius: '3px',
                          backgroundColor: note.status === 'approved' ? '#28a745' : note.status === 'rejected' ? '#dc3545' : '#ffc107',
                          color: 'white',
                          fontWeight: '600'
                        }}>
                          {note.status}
                        </span>
                      </div>
                    </div>

                    {/* Middle section: Note preview */}
                    <div style={{
                      flex: '2 1 250px',
                      fontSize: '12px',
                      color: 'var(--ifm-color-emphasis-800)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: '1.3'
                    }}>
                      {note.content}
                    </div>

                    {/* Right section: Actions */}
                    <div style={{ display: 'flex', gap: '0.35rem', marginLeft: 'auto' }}>
                      <button
                        onClick={() => setSelectedNote(note)}
                        style={{
                          padding: '4px 8px',
                          background: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        style={{
                          padding: '4px 8px',
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '11px'
                        }}
                      >
                        🗑️
                      </button>
                    </div>

                    {/* Rejection reason row (full width if present) */}
                    {note.status === 'rejected' && note.rejection_reason && (
                      <div style={{
                        flex: '1 1 100%',
                        padding: '0.5rem',
                        background: 'rgba(220, 53, 69, 0.1)',
                        border: '1px solid #dc3545',
                        borderRadius: '4px',
                        fontSize: '12px',
                        marginTop: '0.25rem'
                      }}>
                        <strong style={{ color: '#dc3545' }}>Rejected:</strong> {note.rejection_reason}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Issue Details Modal */}
      {selectedIssue && issueDetails && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
          onClick={() => setSelectedIssue(null)}
        >
          <div
            style={{
              backgroundColor: 'var(--ifm-background-color)',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>{issueDetails.issue.title}</h2>
              <button
                onClick={() => setSelectedIssue(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: 'var(--ifm-color-emphasis-600)',
                  padding: '0.25rem'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Class:</strong> {issueDetails.issue.class_name}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Type:</strong> {issueTypeLabels[issueDetails.issue.issue_type]}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Description:</strong>
                <p style={{ marginTop: '0.5rem' }}>{issueDetails.issue.description}</p>
              </div>
            </div>

            {issueDetails.issue.status !== 'closed' && (
              <div style={{ marginBottom: '1.5rem' }}>
                <button
                  onClick={handleCloseIssue}
                  disabled={submitting}
                  style={{
                    padding: '8px 16px',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    opacity: submitting ? 0.6 : 1
                  }}
                >
                  Close Issue
                </button>
              </div>
            )}

            {/* Comments */}
            <div>
              <h3>Comments ({issueDetails.comments.length})</h3>
              <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '1rem' }}>
                {issueDetails.comments.map((comment) => (
                  <div
                    key={comment.id}
                    style={{
                      padding: '1rem',
                      marginBottom: '1rem',
                      backgroundColor: comment.is_moderator_note ? 'rgba(255, 193, 7, 0.1)' : 'var(--ifm-background-surface-color)',
                      border: '1px solid var(--ifm-color-emphasis-300)',
                      borderRadius: '8px'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <img
                        src={comment.avatar}
                        alt={comment.username}
                        style={{ width: '24px', height: '24px', borderRadius: '50%' }}
                      />
                      <strong>{comment.username}</strong>
                      {comment.is_moderator === 1 && <span style={{ fontSize: '0.75rem', color: '#007bff' }}>👑 Moderator</span>}
                      {comment.is_moderator_note === 1 && <span style={{ fontSize: '0.75rem', color: '#ffc107' }}>🔒 Private Note</span>}
                    </div>
                    <p style={{ margin: '0.5rem 0' }}>{comment.comment}</p>
                    <div style={{ fontSize: '0.75rem', color: 'var(--ifm-color-emphasis-600)' }}>
                      {formatDate(comment.created_at)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Comment */}
              {issueDetails.issue.status !== 'closed' && (
                <div>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--ifm-color-emphasis-300)',
                      marginBottom: '0.5rem',
                      background: 'var(--ifm-background-color)',
                      color: 'var(--ifm-font-color-base)'
                    }}
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={submitting || !newComment.trim()}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: 'var(--ifm-color-primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: (submitting || !newComment.trim()) ? 'not-allowed' : 'pointer',
                      opacity: (submitting || !newComment.trim()) ? 0.6 : 1
                    }}
                  >
                    {submitting ? 'Adding...' : 'Add Comment'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Note Details Modal */}
      {selectedNote && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
          onClick={() => setSelectedNote(null)}
        >
          <div
            style={{
              backgroundColor: 'var(--ifm-background-color)',
              borderRadius: '8px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: '24px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h2 style={{ margin: 0 }}>
                {selectedNote.method_name
                  ? `${selectedNote.class_name}::${selectedNote.method_name}()`
                  : selectedNote.property_name
                  ? `${selectedNote.class_name}.${selectedNote.property_name}`
                  : selectedNote.class_name}
              </h2>
              <button
                onClick={() => setSelectedNote(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--ifm-color-emphasis-600)'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <span style={{
                fontSize: '12px',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                backgroundColor: selectedNote.status === 'approved' ? '#28a745' : selectedNote.status === 'rejected' ? '#dc3545' : '#ffc107',
                color: 'white'
              }}>
                {selectedNote.status}
              </span>
            </div>

            <div style={{ marginBottom: '16px', color: 'var(--ifm-color-emphasis-700)', fontSize: '14px' }}>
              <strong>Submitted:</strong> {formatDate(selectedNote.created_at)}
              {selectedNote.approved_at && (
                <>
                  <br />
                  <strong>{selectedNote.status === 'approved' ? 'Approved' : 'Rejected'}:</strong> {formatDate(selectedNote.approved_at)}
                </>
              )}
            </div>

            <div style={{
              background: 'var(--ifm-color-emphasis-100)',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '16px',
              whiteSpace: 'pre-wrap'
            }}>
              {selectedNote.content}
            </div>

            {selectedNote.status === 'rejected' && selectedNote.rejection_reason && (
              <div style={{
                padding: '12px',
                background: 'rgba(220, 53, 69, 0.1)',
                border: '1px solid #dc3545',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <strong style={{ color: '#dc3545' }}>Rejection Reason:</strong>
                <p style={{ margin: '0.5rem 0 0 0' }}>{selectedNote.rejection_reason}</p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => handleDeleteNote(selectedNote.id)}
                style={{
                  padding: '8px 16px',
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                🗑️ Delete Note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
